import { msAPI, manageAPI } from "./index.js";
import moment from "moment";
export const getPlans = async () => {
  return (await msAPI.get("merchant/marketplace/categories")).data;
}

// Unwrap the BE's {data, message, status, version} envelope; fall back when absent.
const unwrap = (body) => body?.data ?? body;

/**
 * Onboarding state for the currently-authenticated marketplace seller.
 * onboarding_step is one of: 'legal' | 'stripe' | 'licenses' | 'menu' | 'under_review'.
 *
 * The licenses step is COMPLETED through this same endpoint. On the licenses
 * "continue" action, pass the three opt-out answers as query params —
 * `liquor_license`, `tobacco_license`, `delivery_license`, each true (needs it)
 * or false (N/A). All three are required together; omitting any → 400. The
 * server then requires ≥1 uploaded document for every required type
 * (business_license, state_registration, w9) plus every opt-out answered true,
 * advances licenses→menu, and returns the updated status. Missing docs → 400
 * with a message naming them (surface it, keep the seller on the licenses step).
 * `other` is never sent/gated.
 *
 * IMPORTANT: only pass these params from the licenses "continue" action — plain
 * mount/poll reads must omit them or they trigger the completion gate early.
 * @param {{liquor_license?: boolean, tobacco_license?: boolean, delivery_license?: boolean}} [params]
 * @returns {Promise<{onboarding: boolean, onboarding_step: string, last_step_completed: string|null, stripe_account_id: string|null, stripe_verified: boolean, legal_signed: boolean, email: string}>}
 */
export const getMarketplaceOnboardingStatus = async (params = {}) => {
  return unwrap(
    (await manageAPI.get("merchant/marketplace/onboarding/status", { params })).data
  );
};

/**
 * Trigger the seller's legal agreement. The BE sends the agreement for
 * signature via BoldSign (idempotent on repeat calls) and returns the email
 * address the signature request was sent to. There is no document to open
 * inline — the seller completes signing from the email BoldSign sends.
 * @returns {Promise<{signer_email: string}>}
 */
export const getMarketplaceLegalAgreement = async () => {
  return unwrap((await manageAPI.get("merchant/marketplace/onboarding/legal/agreement")).data);
};

/**
 * Mark the legal agreement as signed. BE trusts the click; creates the seller's
 * Stripe Connect account and account-link, advances onboarding_step from
 * 'legal' to 'stripe', and bumps last_step_completed. The full Stripe
 * AccountLink object is also persisted to the seller's stripe_account_link
 * field — FE re-reads from selectedLocation after refreshing the merchant data.
 * @returns {Promise<{ok: boolean, stripe_account_link: {url: string, expires_at: string, created: string}}>}
 */
export const acceptMarketplaceLegalAgreement = async () => {
  return unwrap((await manageAPI.post("merchant/marketplace/onboarding/legal/accept")).data);
};
/**
 * License document categories collected during the licenses onboarding step.
 * - `required: true`  → the seller must upload at least one document.
 * - `optOut: true`    → optional category with an "N/A" checkbox; satisfied by
 *                       either checking N/A or uploading a document. These three
 *                       also drive the yes/no status booleans sent on continue
 *                       (param name == `type`, value == !na).
 * - neither           → fully optional (no gating, no status param).
 */
export const MARKETPLACE_LICENSE_TYPES = [
  {
    type: "business_license",
    label: "Local Business License",
    required: true,
    description:
      "Your city or county license to operate: a business license, business tax certificate, or certificate of occupancy issued by your municipality.",
  },
  {
    type: "state_registration",
    label: "State Registration & Tax Permits",
    required: true,
    description:
      "Your state sales/seller's tax permit, your Secretary of State business registration (LLC/corporation formation), and any state tax license for what you sell. Upload all that apply.",
  },
  {
    type: "liquor_license",
    label: "Alcohol Licenses & Permits",
    optOut: true,
    naLabel: "N/A — I don't sell alcohol",
    description:
      "Your alcohol retail license plus any off-premise, online-sales, or delivery endorsement tied to it. Upload every alcohol credential your business holds.",
  },
  {
    type: "tobacco_license",
    label: "Tobacco, Vape & Nicotine Permits",
    optOut: true,
    naLabel: "N/A — I don't sell tobacco, vape, or nicotine products",
    description:
      "Any retail license for cigarettes, cigars, smokeless/chewing tobacco, vapes/ENDS, nicotine pouches, or other alternative-nicotine products, including a separate vape/ENDS license if your state requires one.",
  },
  {
    type: "delivery_license",
    label: "Delivery Permits & Endorsements",
    optOut: true,
    naLabel:
      "N/A — I don't hold a separate delivery permit (my license covers it, or my state doesn't require one)",
    description:
      "Any delivery permit or endorsement your business holds to have your products delivered. For example, a retailer alcohol delivery endorsement, or a vape delivery permit.",
  },
  {
    type: "w9",
    label: "Federal Tax ID (W-9)",
    required: true,
    description:
      "A completed, signed W-9. Sole proprietors without an EIN can use their SSN on the W-9.",
  },
  {
    type: "other",
    label: "Other Documents",
    description:
      "Anything else relevant to what you sell or how you operate that doesn't fit the boxes above.",
  },
];

/**
 * List the license documents already uploaded for the current seller (metadata
 * only — no file bytes). Used to render per-type upload history and to restore
 * progress when the seller returns to this step.
 * @returns {Promise<Array<{id: string, type: string, original_name: string, content_type: string, size: number, uploaded_at: string}>>}
 */
export const getMarketplaceOnboardingLicenses = async () => {
  return unwrap((await manageAPI.get("merchant/marketplace/onboarding/licenses")).data);
};

/**
 * License documents plus license metadata for the current seller. Not gated on
 * onboarding, so it works for live sellers (Settings → Licenses) as well.
 * license_requested_at is a Go zero time ("0001-01-01T00:00:00Z") when unset.
 * @returns {Promise<{onboarding: boolean, onboarding_step: string, licenses: Array<Object>|null, license_requested_at: string, license_answers: {liquor_license: boolean, tobacco_license: boolean, delivery_license: boolean}}>}
 */
export const getMarketplaceLicenses = async () => {
  return unwrap((await manageAPI.get("merchant/marketplace/licenses")).data);
};

/**
 * Upload one license document (multipart). Multiple documents per type are
 * allowed — each call adds one. This does NOT advance the step; the seller
 * completes 'licenses' via getMarketplaceOnboardingStatus() with the opt-out
 * answers. Max 15 MB. Same filename overwrites server-side, so keep names unique.
 * @param {'business_license'|'state_registration'|'liquor_license'|'tobacco_license'|'delivery_license'|'w9'|'other'} type
 * @param {File} file
 * @returns {Promise<{id: string, type: string, bucket: string, key: string, original_name: string, content_type: string, size: number, uploaded_at: string}>}
 */
export const uploadMarketplaceOnboardingLicense = async (type, file) => {
  const form = new FormData();
  form.append("type", type);
  form.append("file", file);
  return unwrap(
    (
      await manageAPI.post("merchant/marketplace/onboarding/licenses", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
    ).data
  );
};

export const uploadMarketplaceOnboardingMenu = async (store_id, file, posSystem) => {
  const form = new FormData();
  form.append("file", file);
  form.append("pos_system", posSystem);
  return unwrap(
    (
      await msAPI.post(`marketplace/onboarding/menu-upload/${store_id}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
    ).data
  );
};
export const getSupportedAPIs = async () => {
  return unwrap((await msAPI.get('/admin/credentials/types')).data);
}
export const createRetailerAPICredentials = async (storeId, payload) => {
  return unwrap((await msAPI.post(`/admin/credentials/add/${storeId}`, payload)).data);
}
export const updateRetailerAPICredentials = async (storeId, payload) => {
  return unwrap((await msAPI.patch(`/admin/credentials/update/${storeId}`, payload)).data);
}
export const deleteRetailerAPICredentials = async (storeId, payload) => {
  return unwrap((await msAPI.delete(`/admin/credentials/remove/${storeId}`, payload)).data);
}
export const testRetailerAPICredentials = async (storeId, payload) => {
  return unwrap((await msAPI.post(`/admin/credentials/test/${storeId}`, payload)).data);
}
export const testRetailerAPICredentialsRaw = async (storeId, payload) => {
  return msAPI.post(`/admin/credentials/test/${storeId}`, payload);
}
export const getLightspeedRetailMasterTokens = async () => {
  return unwrap((await msAPI.get('/admin/credentials/lightspeed-retail/master-tokens')).data);
}
export const getLightspeedShops = async (storeId, payload) => {
  return unwrap((await msAPI.post(`/admin/credentials/lightspeed-retail/shops/${storeId}`, payload)).data);
}
export const getPOSIntegrations= async () => {
  return unwrap((await msAPI.get(`/retailer/pos-integrations/list`)).data);
}